import { useCallback, useEffect, useState } from "preact/hooks";
import type { ComponentChildren, JSX } from "preact";
import { api, type Site } from "../../api";
import { useI18n } from "../../lib/i18n";
import { apiErrorText } from "../../lib/apiError";
import { CheckIcon, CopyIcon } from "../../components/Icons";

/**
 * Pieces every settings tab uses: the list of sites, a site picker, a switch,
 * and a status line that says what just happened.
 */

export function useSites(): {
  sites: Site[];
  reload: () => Promise<void>;
  error: string | null;
} {
  const [sites, setSites] = useState<Site[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();
  const failed = t.common.sitesFailed;
  const reload = useCallback(async () => {
    try {
      setSites((await api.sites()).sites);
      setError(null);
    } catch {
      setError(failed);
    }
  }, [failed]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { sites, reload, error };
}

/** Remembers the chosen site while the tab is open; defaults to the first. */
export function useSelectedSite(sites: Site[]): [Site | null, (id: number) => void] {
  const [id, setId] = useState<number | null>(null);
  const site = sites.find((s) => s.id === id) ?? sites[0] ?? null;
  return [site, setId];
}

export function SitePicker({
  sites,
  value,
  onChange,
}: {
  sites: Site[];
  value: Site | null;
  onChange: (id: number) => void;
}): JSX.Element | null {
  const { t } = useI18n();
  if (sites.length <= 1) return null;
  return (
    <label class="site-picker">
      {t.common.site}
      <select value={value?.id ?? ""} onChange={(e) => onChange(Number(e.currentTarget.value))}>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A yes/no setting, laid out like every other setting row: what it is and
 * why on the left, the switch on the right. The whole row is the label, so
 * a click anywhere on it flips the switch.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label class={`setting switch-setting${disabled ? " disabled" : ""}`}>
      <span class="setting-text">
        <span class="setting-title">{label}</span>
        {hint ? <span class="hint block">{hint}</span> : null}
      </span>
      <span class="setting-control">
        <input
          type="checkbox"
          role="switch"
          class="switch"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      </span>
    </label>
  );
}

/**
 * One setting: what it is and why on the left, the control on the right.
 * `wide` puts a control that needs the full width (a code block, a group of
 * fields, a list) under the text instead.
 */
export function SettingRow({
  title,
  hint,
  htmlFor,
  wide,
  children,
}: {
  title: string;
  hint?: ComponentChildren;
  htmlFor?: string;
  wide?: boolean;
  children: ComponentChildren;
}): JSX.Element {
  return (
    <div class={`setting${wide ? " wide" : ""}`}>
      <div class="setting-text">
        {htmlFor ? <label for={htmlFor}>{title}</label> : <span class="setting-title">{title}</span>}
        {hint ? <p class="hint">{hint}</p> : null}
      </div>
      <div class="setting-control">{children}</div>
    </div>
  );
}

/** A switch without its own label, for a row that already says what it is. */
export function Toggle({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <input
      type="checkbox"
      role="switch"
      class="switch"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
}

/** A value to paste somewhere else, with a button that does the copying. */
export function CopyBox({ value, compact }: { value: string; compact?: boolean }): JSX.Element {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <div class={`copy-box${compact ? " compact" : ""}`}>
      <code>{value}</code>
      <button
        type="button"
        class="btn sm"
        onClick={() =>
          void navigator.clipboard
            ?.writeText(value)
            .then(() => setCopied(true))
            .catch(() => undefined)
        }
      >
        {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
        {copied ? t.sites.copied : t.sites.copy}
      </button>
    </div>
  );
}

export type Status = { tone: "ok" | "error"; text: string } | null;

export function useStatus(): [Status, (tone: "ok" | "error", text: string) => void, (err: unknown) => void] {
  const [status, setStatus] = useState<Status>(null);
  const { t } = useI18n();
  const fallback = t.common.saveFailed;
  const show = useCallback((tone: "ok" | "error", text: string) => {
    setStatus({ tone, text });
    if (tone === "ok") window.setTimeout(() => setStatus((s) => (s?.text === text ? null : s)), 3000);
  }, []);
  const fail = useCallback((err: unknown) => show("error", apiErrorText(err, t, fallback)), [show, fallback, t]);
  return [status, show, fail];
}

export function StatusLine({ status }: { status: Status }): JSX.Element | null {
  if (!status) return null;
  return (
    <div class={`alert${status.tone === "error" ? " error" : ""}`} role="status">
      {status.text}
    </div>
  );
}

/**
 * A card of related settings: a title, a line saying what they are for, and
 * optionally actions on the right of the title (an "add" button, a filter).
 */
export function Section({
  title,
  intro,
  actions,
  danger,
  children,
}: {
  title: string;
  intro?: ComponentChildren;
  actions?: ComponentChildren;
  danger?: boolean;
  children: ComponentChildren;
}): JSX.Element {
  return (
    <section class={`panel${danger ? " is-danger" : ""}`}>
      <div class="panel-head">
        <div class="panel-head-text">
          <h2>{title}</h2>
          {intro ? <p class="intro">{intro}</p> : null}
        </div>
        {actions ? <div class="panel-head-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * A window in front of the page, for making or changing one thing — a new
 * saved reply, a new team member — without the form living at the foot of
 * the page. Escape and a click outside close it.
 */
export function Modal({
  title,
  intro,
  wide,
  onClose,
  children,
}: {
  title: string;
  intro?: string;
  wide?: boolean;
  onClose: () => void;
  children: ComponentChildren;
}): JSX.Element {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div class="dialog-scrim" onClick={onClose}>
      <div
        class={`dialog form-dialog${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{title}</h2>
        {intro ? <p class="dialog-body">{intro}</p> : null}
        {children}
      </div>
    </div>
  );
}

export function ReadOnlyNote({ isAdmin }: { isAdmin: boolean }): JSX.Element | null {
  const { t } = useI18n();
  if (isAdmin) return null;
  return <div class="alert warn">{t.common.readOnly}</div>;
}
